/**
 * Favorite Utilities
 *
 * Shared utility functions for handling user favorites across the application.
 * This centralizes the logic for determining favorite types and generating keys,
 * ensuring consistency between components and reducers.
 */

import { FavoriteType } from 'src/types/userPreferences/userPreferences.types';

/**
 * Represents a node that can be favorited in the product hierarchy.
 * This interface describes the minimum properties needed to identify a favorite.
 */
export interface FavoriteableNode {
  /** The hierarchy level of the node */
  hierarchy_level: 'PRODUCT_LINE' | 'RPN' | 'FPN';
  /** Product line ID (always present) */
  product_line_id: number;
  /** RPN ID (present for RPN and FPN nodes) */
  rpn_id?: number;
  /** FPN ID (present for FPN nodes) */
  fpn_id?: number;
}

/**
 * Result of determining the favorite type and ID for a node
 */
export interface FavoriteTypeAndId {
  /** The FavoriteType enum value */
  favoriteType: FavoriteType;
  /** The numeric ID of the item */
  itemId: number;
}

/**
 * Generates a unique identifier key for a favoriteable node.
 *
 * This key format is used by the favorites set selector to enable O(1) lookup
 * of favorite status. The format matches the pattern used in
 * `selectFavoritesSet` in userPreferencesSelectors.ts.
 *
 * @param node - The favoriteable node (ProductLine, RPN, or FPN)
 * @returns A unique string key in format "{type}_{id}" (e.g., "product_line_123", "rpn_456", "fpn_789")
 *          Returns empty string if the node type cannot be determined
 *
 * @example
 * // Product Line node
 * getFavoriteKey({ hierarchy_level: 'PRODUCT_LINE', product_line_id: 123 })
 * // Returns: "product_line_123"
 *
 * @example
 * // RPN node
 * getFavoriteKey({ hierarchy_level: 'RPN', product_line_id: 100, rpn_id: 456 })
 * // Returns: "rpn_456"
 *
 * @example
 * // FPN node
 * getFavoriteKey({ hierarchy_level: 'FPN', product_line_id: 100, rpn_id: 200, fpn_id: 789 })
 * // Returns: "fpn_789"
 */
export const getFavoriteKey = (node: FavoriteableNode): string => {
  switch (node.hierarchy_level) {
    case 'PRODUCT_LINE':
      return `product_line_${node.product_line_id}`;
    case 'RPN':
      if (node.rpn_id !== undefined) {
        return `rpn_${node.rpn_id}`;
      }
      break;
    case 'FPN':
      if (node.fpn_id !== undefined) {
        return `fpn_${node.fpn_id}`;
      }
      break;
  }
  return '';
};

/**
 * Determines the FavoriteType enum value and item ID from a favoriteable node.
 *
 * This function maps hierarchy levels to the corresponding FavoriteType enum values
 * and extracts the appropriate item ID for API calls.
 *
 * @param node - The favoriteable node (ProductLine, RPN, or FPN)
 * @returns An object containing the FavoriteType and itemId, or null if the type cannot be determined
 *
 * @example
 * // Product Line node
 * getFavoriteTypeAndId({ hierarchy_level: 'PRODUCT_LINE', product_line_id: 123 })
 * // Returns: { favoriteType: FavoriteType.PRODUCT_LINE, itemId: 123 }
 *
 * @example
 * // RPN node
 * getFavoriteTypeAndId({ hierarchy_level: 'RPN', product_line_id: 100, rpn_id: 456 })
 * // Returns: { favoriteType: FavoriteType.RPN, itemId: 456 }
 *
 * @example
 * // Invalid node (missing rpn_id)
 * getFavoriteTypeAndId({ hierarchy_level: 'RPN', product_line_id: 100 })
 * // Returns: null
 */
export const getFavoriteTypeAndId = (node: FavoriteableNode): FavoriteTypeAndId | null => {
  switch (node.hierarchy_level) {
    case 'PRODUCT_LINE':
      return { favoriteType: FavoriteType.PRODUCT_LINE, itemId: node.product_line_id };
    case 'RPN':
      if (node.rpn_id !== undefined) {
        return { favoriteType: FavoriteType.RPN, itemId: node.rpn_id };
      }
      break;
    case 'FPN':
      if (node.fpn_id !== undefined) {
        return { favoriteType: FavoriteType.FPN, itemId: node.fpn_id };
      }
      break;
  }
  return null;
};

/**
 * Type guard to check if a node has the required properties for favoriting.
 *
 * @param node - The node to check
 * @returns True if the node has all required properties for its hierarchy level
 *
 * @example
 * const node = { hierarchy_level: 'RPN', product_line_id: 100, rpn_id: 456 };
 * if (isFavoriteableNode(node)) {
 *   const key = getFavoriteKey(node); // Safe to call
 * }
 */
export const isFavoriteableNode = (node: unknown): node is FavoriteableNode => {
  if (!node || typeof node !== 'object') {
    return false;
  }

  const n = node as Record<string, unknown>;

  if (!('hierarchy_level' in n) || !('product_line_id' in n)) {
    return false;
  }

  const hierarchyLevel = n.hierarchy_level;
  const productLineId = n.product_line_id;

  if (typeof productLineId !== 'number') {
    return false;
  }

  switch (hierarchyLevel) {
    case 'PRODUCT_LINE':
      return true;
    case 'RPN':
      return 'rpn_id' in n && typeof n.rpn_id === 'number';
    case 'FPN':
      return 'fpn_id' in n && typeof n.fpn_id === 'number';
    default:
      return false;
  }
};
